import {
  AuthenticationResult,
  BrowserAuthError,
  createNestablePublicClientApplication,
  type IPublicClientApplication,
} from "@azure/msal-browser";
import { msalConfig } from "./MSALConfig";
import { createLocalUrl } from "../utils/Helpers";
import { getTokenRequest } from "./MSALCommon";

export type AuthDialogResult = {
  idToken?: string;
  error?: string;
};

type DialogEventMessage = { message: string; origin: string | undefined };
type DialogEventError = { error: number };
type DialogEventArg = DialogEventMessage | DialogEventError;

export class AccountManager {
  private pca: IPublicClientApplication | undefined = undefined;
  private _dialogApiResult: Promise<AuthDialogResult> | null = null;
  private _initialized = false;

  async init(): Promise<void> {
    if (this._initialized) return;
    this._initialized = true;

    await Office.onReady();
    this.pca = await createNestablePublicClientApplication(msalConfig);
  }

  // Used for Login with Azure AD and SSO
  async getUserToken(): Promise<string> {
    if (!this._initialized) {
      await this.init();
    }

    const { idToken } = await this.getAuthResult(["user.read"]);

    if (!idToken) {
      throw new Error("Token not found");
    }

    return idToken;
  }

  async getAuthResult(scopes: string[]): Promise<AuthenticationResult | AuthDialogResult> {
    if (this._dialogApiResult) {
      return this._dialogApiResult;
    }

    if (this.pca === undefined) {
      throw new Error("AccountManager is not initialized!");
    }

    try {
      const authResult = await this.pca.acquireTokenSilent(getTokenRequest(scopes, false));
      return authResult;
    } catch (error) {
      console.warn(`Unable to acquire token silently: ${error}`);
    }

    try {
      const selectAccount = this.pca.getActiveAccount() ? false : true;
      const authResult = await this.pca.acquireTokenPopup(getTokenRequest(scopes, selectAccount));

      if (selectAccount) {
        this.pca.setActiveAccount(authResult.account);
      }

      return authResult;
    } catch (popupError) {
      if (popupError instanceof BrowserAuthError && popupError.errorCode === "popup_window_error") {
        const authResult = await this.getTokenWithDialogApi();
        return authResult;
      } else {
        throw new Error(`Unable to acquire access token: ${popupError}`);
      }
    }
  }

  async getTokenWithDialogApi(): Promise<AuthDialogResult> {
    this._dialogApiResult = new Promise((resolve, reject) => {
      Office.context.ui.displayDialogAsync(createLocalUrl("dialog.html"), { height: 60, width: 30 }, (result) => {
        result.value.addEventHandler(Office.EventType.DialogEventReceived, (arg: DialogEventArg) => {
          const errorArg = arg as DialogEventError;
          if (errorArg.error == 12006) {
            this._dialogApiResult = null;
            reject("Dialog closed");
          }
        });
        result.value.addEventHandler(Office.EventType.DialogMessageReceived, (arg: DialogEventArg) => {
          const messageArg = arg as DialogEventMessage;
          const parsedMessage = JSON.parse(messageArg.message);
          result.value.close();

          if (parsedMessage.error) {
            reject(parsedMessage.error);
            this._dialogApiResult = null;
          } else {
            resolve(parsedMessage.idToken);
          }
        });
      });
    });
    return this._dialogApiResult as AuthDialogResult;
  }
}

export default new AccountManager();
